// src/lib/auth/authErrorMessage.js
//
// 회원가입/로그인 등 인증 플로우의 백엔드 에러 응답을 한글 메시지로 매핑한다.
// 백엔드가 반환하는 영문 `detail` 은 사용자에게 보여주는 문구가 아니라 "에러 식별자"다.
// 이 값을 화면에 그대로 노출하면 안 되므로, detail 문자열을 키로 한글 문구를 매핑한다.
//
// detail 은 백엔드 계약(안정적 식별자)이라 임의로 바뀌지 않는다. 단 표에 없는 detail 은
// 호출자가 넘긴 fallback(없으면 generic)으로 처리한다. (소스: GF_Backend user/account 엔드포인트)

// detail(영문, 소문자/공백 정규화) → 한글 노출 문구
const DETAIL_MESSAGES = {
  // POST /user/email/send-code · POST /user/signup (409) — 동일 문구
  'email already exists': '이미 가입된 이메일입니다. 로그인해 주세요.',
  // POST /user/email/send-code (500)
  'failed to send verification email':
    '인증 메일 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.',
  // POST /user/email/verify-code (400)
  'invalid verification token':
    '인증 정보가 올바르지 않습니다. 인증을 다시 요청해 주세요.',
  'email mismatch': '이메일이 일치하지 않습니다.',
  'invalid code': '인증 코드가 올바르지 않습니다.',
  'code expired': '인증 코드가 만료되었습니다. 다시 요청해 주세요.',
  // POST /user/signup (400)
  'password required': '비밀번호를 입력해 주세요.',
  // POST /user/login (401 / 403)
  'invalid credentials': '이메일 또는 비밀번호가 올바르지 않습니다.',
  'user is not active': '사용할 수 없는 계정입니다. 고객센터로 문의해 주세요.',
};

const GENERIC_FALLBACK = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
const NETWORK_FALLBACK = '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

/**
 * axios 에러를 사용자에게 보여줄 한글 메시지로 변환한다.
 *
 * @param {object} error    - axios catch 로 받은 에러 객체
 * @param {string} fallback - 매핑표에 없는 detail 일 때 노출할 문구 (기본: generic)
 * @returns {string} 한글 에러 메시지
 */
export function mapAuthError(error, fallback = GENERIC_FALLBACK) {
  // 응답이 없으면 네트워크/요청 단계 오류
  if (!error || !error.response) return NETWORK_FALLBACK;

  const data = error.response.data;
  // 422(Pydantic) 등에서 detail 은 배열/객체일 수 있다 → 문자열일 때만 키로 사용
  const detail =
    typeof data?.detail === 'string' ? data.detail.trim().toLowerCase() : '';

  if (detail && DETAIL_MESSAGES[detail]) return DETAIL_MESSAGES[detail];

  // 표에 없는 detail(미래 추가분, 422 validation 등)은 호출자 fallback 으로 안내
  return fallback;
}

export default mapAuthError;
